import React, { useEffect, useRef, useState } from "react";
import { Container, Button, Alert, Table, Card } from "react-bootstrap";
import { BrowserMultiFormatReader } from "@zxing/browser";

const STORAGE_KEY = "shoolin.barcodes.v1";

function loadHistory() {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
function saveHistory(h) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(h)); } catch {}
}

function BarcodeScanner() {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const lastResultRef = useRef({ text: "", at: 0 });

  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [history, setHistory] = useState(loadHistory);

  useEffect(() => {
    return () => {
      try { controlsRef.current?.stop(); } catch {}
    };
  }, []);

  const startScan = async () => {
    setError("");
    setStatus("Requesting camera…");
    try {
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromConstraints(
        {
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        },
        videoRef.current,
        (result) => {
          if (!result) return;
          const text = result.getText();
          const now = Date.now();
          if (text === lastResultRef.current.text && now - lastResultRef.current.at < 1500) return;
          lastResultRef.current = { text, at: now };

          if (navigator.vibrate) navigator.vibrate(40);

          setHistory((prev) => {
            const next = [
              { text, format: result.getBarcodeFormat?.() ?? "", at: now },
              ...prev,
            ].slice(0, 50);
            saveHistory(next);
            return next;
          });
        }
      );
      controlsRef.current = controls;
      setScanning(true);
      setStatus("Point the camera at a barcode");
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError("Camera permission denied. Allow camera access in your browser settings.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("No camera found on this device.");
      } else if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
        setError("Camera requires HTTPS. Open this page over https:// (or use localhost).");
      } else {
        setError(e?.message || "Could not start camera.");
      }
      setStatus("");
    }
  };

  const stopScan = () => {
    try { controlsRef.current?.stop(); } catch {}
    controlsRef.current = null;
    setScanning(false);
    setStatus("");
  };

  const clearHistory = () => {
    setHistory([]);
    saveHistory([]);
  };

  const copy = async (text) => {
    try { await navigator.clipboard?.writeText(text); } catch {}
  };

  const lookupInPricebook = (text) => {
    window.open(`/pricebook?q=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <Container className="invoice-upload-container mt-4">
      <h2 className="mb-4">Barcode Scanner</h2>

      <Card className="p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Camera</h5>
          {scanning ? (
            <Button variant="outline-danger" size="sm" onClick={stopScan}>Stop</Button>
          ) : (
            <Button variant="primary" size="sm" onClick={startScan}>Start scanning</Button>
          )}
        </div>

        <div
          style={{
            position: "relative",
            background: "#000",
            borderRadius: 8,
            overflow: "hidden",
            aspectRatio: "16 / 9",
            display: scanning ? "block" : "none",
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(78%, 320px)",
              height: "min(40%, 180px)",
              border: "2px solid rgba(255,255,255,0.9)",
              borderRadius: 12,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.32)",
              pointerEvents: "none",
            }}
          />
          {status && (
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 8,
                textAlign: "center",
                color: "#fff",
                fontSize: 14,
                textShadow: "0 1px 2px rgba(0,0,0,0.6)",
              }}
            >
              {status}
            </div>
          )}
        </div>

        {!scanning && !error && (
          <div className="text-muted small">
            Click <strong>Start scanning</strong> to enable the camera. On iPhone, the page must be served over HTTPS.
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mb-0 mt-3" dismissible onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </Card>

      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Scanned codes</h5>
          {history.length > 0 && (
            <Button variant="outline-secondary" size="sm" onClick={clearHistory}>
              Clear
            </Button>
          )}
        </div>

        {history.length === 0 ? (
          <div className="text-muted">Nothing scanned yet.</div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Code</th>
                  <th style={{ width: 120 }}>Format</th>
                  <th style={{ width: 160 }}>Time</th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={`${h.at}-${i}`}>
                    <td>{i + 1}</td>
                    <td style={{ fontFamily: "ui-monospace, Menlo, monospace", wordBreak: "break-all" }}>
                      {h.text}
                    </td>
                    <td>{h.format || "—"}</td>
                    <td>{new Date(h.at).toLocaleTimeString()}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button variant="outline-primary" size="sm" onClick={() => lookupInPricebook(h.text)}>
                          Pricebook
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => copy(h.text)}>
                          Copy
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </Container>
  );
}

export default BarcodeScanner;
